/**
 * Sales Taxes and Charges — the grid, and the totals it produces.
 *
 * Every charge type the doctype's Select allows, with the arithmetic in `lib/salesTaxes.ts` and
 * pinned there against Akhil's worked example. This component only edits rows and renders what the
 * engine returns; it computes nothing itself, so the figures on screen are the figures that will
 * be sent.
 *
 * ---- Rows are held as a DRAFT and saved once ----
 *
 * `taxes` replaces wholesale on the wire, so every keystroke cannot be a write: a half-typed rate
 * of "2" on the way to "2.5" would otherwise be saved, recompute the customer's grand total, and be
 * saved again. The card stages edits and has an explicit Save, the same pattern the Settings cards
 * use for the same reason.
 */

import { useEffect, useState } from 'react'
import { viewMoney } from '../../lib/viewMoney'
import type { WireFx } from '../../lib/api/wire'
import { Card, Button } from '../../components/ui'
import { LinkCombo } from '../../components/LinkPicker'
import { formatMoney } from '../../lib/format'
import { computeQuotationTotals, type DiscountInput } from '../../lib/salesTaxes'
import { useAccountPicker } from '../../lib/api/useEnquiryFromMail'
import type { ChargeType, Currency, QuotationTaxRow } from '../../lib/types'

const CHARGE_TYPES: ChargeType[] = [
  'Actual',
  'On Net Total',
  'On Previous Row Amount',
  'On Previous Row Total',
  'On Item Quantity',
]

/** What each type actually does, said where it is chosen rather than in documentation nobody opens. */
const CHARGE_HELP: Record<ChargeType, string> = {
  Actual: 'A flat amount you type. Never calculated.',
  'On Net Total': 'rate % of the net total.',
  'On Previous Row Amount': 'rate % of the referenced row’s own amount.',
  'On Previous Row Total': 'rate % of the running total at the referenced row.',
  'On Item Quantity': 'rate × total quantity.',
}

/** A "Previous Row" charge is the only type that needs a reference. */
const needsRowId = (type: ChargeType) =>
  type === 'On Previous Row Amount' || type === 'On Previous Row Total'

export function TaxesCard({
  rows,
  netTotal,
  totalQty,
  discount,
  currency,
  viewCurrency,
  fx,
  saving,
  onSave,
  company,
}: {
  rows: readonly QuotationTaxRow[]
  netTotal: number
  totalQty: number
  discount: DiscountInput | null
  /** The DOCUMENT's currency — what an "Actual" charge is typed in and what a save writes. */
  currency: Currency
  /**
   * The currency the picker shows. The grid's figures stay the document's (that is what a save
   * writes) and are converted for display — an "Actual" amount typed here is read back into the
   * document's currency (`viewMoney`).
   */
  viewCurrency: Currency
  fx: WireFx
  /**
   * A write is in flight — DIMS only, never disables. `busy` here is the SHARED
   * `updateTerms.isPending`, so a commission field committing on blur would otherwise freeze
   * this taxes grid mid-typing. Same trap the commission panel documents.
   */
  saving: boolean
  onSave: (rows: QuotationTaxRow[]) => void
  /** The quotation's company — the account heads offered are that company's only. */
  company: string
}) {
  const useCompanyAccounts = (search: string) => useAccountPicker(search, company)
  /* Collapsed by default, like the commission and terms cards beside it — three expanded panels
     pushed the line table off the screen on the one page whose job is the lines. */
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<QuotationTaxRow[]>([...rows])
  // Re-seeded when the server's rows change beneath us — a save elsewhere, or a reload.
  useEffect(() => setDraft([...rows]), [rows])

  const dirty = JSON.stringify(draft) !== JSON.stringify(rows)
  const view = viewMoney(currency, viewCurrency, fx)
  const shown = (amount: number) => formatMoney({ amount: view.toView(amount), currency: view.currency })
  const computed = computeQuotationTotals({ netTotal, totalQty, taxes: draft, discount })

  const patch = (id: string, next: Partial<QuotationTaxRow>) =>
    setDraft((d) => d.map((r) => (r.id === id ? { ...r, ...next } : r)))

  const addRow = () =>
    setDraft((d) => [
      ...d,
      {
        id: `new-${Date.now()}`,
        idx: d.length + 1,
        charge_type: 'Actual',
        account_head: '',
        tax_rate: 0,
        tax_amount: 0,
        row_id: '',
        total: 0,
      },
    ])

  return (
    <Card style={{ marginBottom: 14, ...(saving ? { opacity: 0.6 } : {}) }}>
      <div
        role="button"
        // Still opens when the page is read-only — see `ReadOnlyGate`.
        data-readonly-allow=""
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpen((v) => !v)
          }
        }}
        style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
      >
        <b>🧾 Taxes and charges</b>
        {/* Readable closed: these change what the customer pays, and a folded card that says
            nothing is how a quotation goes out with a charge nobody meant to leave on. */}
        <span style={{ color: 'var(--muted)', fontSize: 12.5 }}>
          {draft.length === 0 ? (
            'none'
          ) : (
            <>
              {draft.length} charge{draft.length === 1 ? '' : 's'} ·{' '}
              <b style={{ color: 'var(--ink)' }}>{shown(computed.totalTax)}</b>
            </>
          )}
          {dirty ? <b style={{ color: 'var(--warn)' }}> · unsaved</b> : null}
        </span>
        <span style={{ flex: 1 }} />
        {/* Save stays reachable while collapsed — an unsaved change must never need the panel
            reopened to commit it. `stopPropagation`, or the click would fold the card instead. */}
        {dirty ? (
          <span onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: 8 }}>
            <Button size="sm" onClick={() => setDraft([...rows])} disabled={false}>
              Discard
            </Button>
            <Button size="sm" variant="primary" disabled={false} onClick={() => onSave(draft)}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </span>
        ) : null}
        <span style={{ color: 'var(--muted)' }}>{open ? '▴' : '▾'}</span>
      </div>

      {!open ? null : (
        <>
      <div style={{ color: 'var(--muted)', fontSize: 12.5, margin: '10px 0' }}>
        Each row adds to the grand total. Order matters — a “Previous Row” charge is calculated
        from a row above it.
      </div>
      {view.missingRate ? (
        <div style={{ color: 'var(--warn)', fontSize: 12.5, margin: '0 0 10px' }}>
          No {currency} → {viewCurrency} rate on file — amounts here are still {currency}.
        </div>
      ) : null}

      {draft.length === 0 ? (
        <div
          style={{
            color: 'var(--muted)',
            fontSize: 12.5,
            padding: '14px 12px',
            border: '1px dashed var(--line)',
            borderRadius: 10,
            textAlign: 'center',
          }}
        >
          No taxes or charges — the grand total is the net total.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ width: 36 }}>#</th>
                <th style={{ width: 190 }}>Type</th>
                <th>Account head</th>
                <th style={{ width: 90, textAlign: 'right' }}>Rate</th>
                <th style={{ width: 120, textAlign: 'right' }}>Amount</th>
                <th style={{ width: 130, textAlign: 'right' }}>Total</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {draft.map((row, i) => {
                const result = computed.taxes[i]
                return (
                  <tr key={row.id}>
                    <td className="mono">{i + 1}</td>
                    <td>
                      <select
                        value={row.charge_type}
                        title={CHARGE_HELP[row.charge_type]}
                        onChange={(e) => patch(row.id, { charge_type: e.target.value as ChargeType })}
                      >
                        {CHARGE_TYPES.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                      {needsRowId(row.charge_type) ? (
                        <select
                          value={row.row_id}
                          aria-label="Row this charge refers to"
                          style={{ marginTop: 4 }}
                          onChange={(e) => patch(row.id, { row_id: e.target.value })}
                        >
                          <option value="">refers to…</option>
                          {/* Only rows ABOVE this one: a charge cannot be calculated from a row
                              that has not been calculated yet, and offering it would produce a
                              silent zero. */}
                          {draft.slice(0, i).map((r, j) => (
                            <option key={r.id} value={String(j + 1)}>
                              row {j + 1}
                            </option>
                          ))}
                        </select>
                      ) : null}
                    </td>
                    <td>
                      <LinkCombo
                        value={row.account_head}
                        placeholder="Search accounts…"
                        useOptions={useCompanyAccounts}
                        onChange={(account_head) => patch(row.id, { account_head })}
                      />
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <input
                        type="number"
                        step="0.001"
                        style={{ width: 74, textAlign: 'right' }}
                        value={row.tax_rate}
                        // An Actual charge is typed, never rated — the field would be inert.
                        disabled={row.charge_type === 'Actual'}
                        aria-label={`Rate for row ${i + 1}`}
                        onChange={(e) => patch(row.id, { tax_rate: Number(e.target.value) })}
                      />
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {row.charge_type === 'Actual' ? (
                        <input
                          type="number"
                          step="0.01"
                          style={{ width: 100, textAlign: 'right' }}
                          value={view.toView(row.tax_amount)}
                          aria-label={`Amount for row ${i + 1} (${view.currency})`}
                          onChange={(e) => patch(row.id, { tax_amount: view.fromView(Number(e.target.value)) })}
                        />
                      ) : (
                        // Computed, so shown rather than typed — an editable box holding a derived
                        // number invites an edit that the next recompute silently discards.
                        <span className="mono">{shown(result?.tax_amount ?? 0)}</span>
                      )}
                    </td>
                    <td className="mono" style={{ textAlign: 'right', color: 'var(--muted)' }}>
                      {shown(result?.running_total ?? 0)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        type="button"
                        className="btn sm dan"
                        title="Remove this charge"
                        onClick={() => setDraft((d) => d.filter((r) => r.id !== row.id))}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
        <Button size="sm" onClick={addRow} disabled={false}>
          ＋ Add charge
        </Button>
        <span style={{ flex: 1 }} />
        <span style={{ color: 'var(--muted)', fontSize: 12.5 }}>
          Taxes and charges: <b className="mono">{shown(computed.totalTax)}</b>
        </span>
        {computed.discountAmount > 0 ? (
          <span style={{ color: 'var(--muted)', fontSize: 12.5 }}>
            · discount <b className="mono">−{shown(computed.discountAmount)}</b>
          </span>
        ) : null}
        <span style={{ fontSize: 13 }}>
          Grand total: <b className="mono">{shown(computed.grandTotal)}</b>
        </span>
      </div>
        </>
      )}
    </Card>
  )
}
